import CustomField from "components/Common/CustomField"
import CustomLabel from "components/Common/CustomLabel"
import { ErrorMessage, Field, Form, Formik } from "formik"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
import * as Yup from "yup"
import { showAlert } from "store/reducers/alertReducer"

import { addUser, getUserDetails, updateUser } from "store/actions/userAction"
import { useNavigate } from "react-router-dom"
import {
  arabicEnglishDigitRegex,
  arabicEnglishDigitRegexTenDigit,
} from "components/Common/Utils"
import Spinners from "components/Shared/Spinners"
import { getRolesLists } from "store/actions/roleAction"
import { usePermissions } from "hooks"
import BackButton from "components/Shared/BackButton"

const initialValues = {
  first_name: "",
  middle_name: "",
  last_name: "",
  phone_code: "+966",
  phone_number: "",
  email: "",
  password: "",
  national_id: "",
  role_uuid: "",
}

const CreateUser = ({ mode }) => {
  const { showAll, permissions } = usePermissions("staff_users")
  const { t } = useTranslation()
  document.title =
    mode === "add" ? t("Create Users | Yamama") : t("Update Users | Yamama")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const uuid = searchParams.get("uuid")

  const { isGettingDetails, isLoading, userDetails } = useSelector(
    state => state.Users
  )
  const { allRoles: rolesList } = useSelector(
    state => state.RolesAndPermissions
  )
  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    const getDetails = () => {
      dispatch(getUserDetails(uuid))
    }
    const fetchRolesList = () => {
      dispatch(getRolesLists())
    }
    mode === "update" && getDetails()
    fetchRolesList()
  }, [mode])

  useEffect(() => {
    if (!isGettingDetails && userDetails && mode === "update") {
      const {
        first_name,
        middle_name,
        last_name,
        email,
        national_id,
        phone_number,
        role,
      } = userDetails

      setFormValues(prev => ({
        first_name,
        middle_name,
        last_name,
        phone_code: "+966",
        phone_number,
        email,
        password: "",
        national_id,
        role_uuid: role?.uuid || "",
      }))
    }
  }, [mode, userDetails])

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(t("Field is required")),
    last_name: Yup.string().required(t("Field is required")),
    phone_number: Yup.string()
      .matches(
        arabicEnglishDigitRegex,
        t("Please make sure to enter a 9 or 10-digit mobile number")
      )
      .required(t("Mobile number is required")),
    national_id: Yup.string()
      .matches(
        arabicEnglishDigitRegexTenDigit,
        t("National Id number must be exactly 10 digits")
      )
      .required(t("National Id number is required")),
    email: Yup.string()
      .email(t("Invalid email"))
      .required(t("Email is required"))
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        t("Please enter a valid email address")
      ),
    password:
      mode === "add"
        ? Yup.string()
            .required(t("Password is required"))
            .min(8, t("Password must be at least 8 characters long"))
            .matches(/[A-Z]/, t("Must contain an uppercase letter (A-Z)"))
            .matches(/[a-z]/, t("Must contain a lowercase letter (a-z)"))
            .matches(/\d/, t("Must contain a number (0-9)"))
            .matches(
              /[!@#$%^&*(),.?":{}|<>]/,
              t("Must contain a special character (@, #, !, ...)")
            )
        : Yup.string(), // No validation if mode is 'update'
    role_uuid: Yup.string().required(t("Field is required")),
  })
  const handleOnSubmit = values => {
    if (mode === "add") {
      dispatch(addUser(values))
        .then(action => {
          const { statusCode, success, message } = action?.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "update") {
      const { password, ...payload } = values

      const updatedPayload = {
        uuid,
        payload,
      }

      dispatch(updateUser(updatedPayload))
        .then(action => {
          const { statusCode, success, message } = action?.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        {mode === "update" && isGettingDetails ? (
          <Spinners />
        ) : (
          <Container fluid>
            <h4 className="card-title ">
              <BackButton />
              {mode === "add" ? t("Add User") : t("Update User")}
            </h4>
            <Card className="theme_card">
              <CardBody>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={validationSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, errors, touched, setFieldValue }) => (
                    <Form>
                      <Row>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="first_name"
                              label={t("First Name")}
                              isRequired={true}
                            />
                            <CustomField
                              name="first_name"
                              type="text"
                              placeholder={t("First Name")}
                              touched={touched}
                              errors={errors}
                            />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="middle_name"
                              label={t("Middle Name")}
                            />
                            <CustomField
                              name="middle_name"
                              type="text"
                              placeholder={t("Middle Name")}
                              touched={touched}
                              errors={errors}
                            />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="last_name"
                              label={t("Last Name")}
                              isRequired={true}
                            />
                            <CustomField
                              name="last_name"
                              type="text"
                              placeholder={t("Last Name")}
                              touched={touched}
                              errors={errors}
                            />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="phone_number"
                              label={t("Mobile number")}
                              isRequired={true}
                            />
                            <div className="d-flex gap-2">
                              <Field
                                as="select"
                                name="phone_code"
                                className="form-control phone_code"
                                style={{ maxWidth: 70 }}
                              >
                                <option value={"+966"} label={"+966"} />
                              </Field>
                              <div className="w-100">
                                <Field
                                  name="phone_number"
                                  type={"tel"}
                                  placeholder={t("500000000")}
                                  className={`form-control ${
                                    errors?.phone_number &&
                                    touched?.phone_number
                                      ? "is-invalid"
                                      : ""
                                  } `}
                                  readOnly={mode === "update"}
                                />
                                <ErrorMessage
                                  name={"phone_number"}
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="email"
                              label={t("Email")}
                              isRequired={true}
                            />
                            <CustomField
                              name="email"
                              type="email"
                              placeholder={t("Email")}
                              touched={touched}
                              errors={errors}
                              isReadOnly={mode === "update"}
                            />
                          </div>
                        </Col>
                        {mode === "add" && (
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="password"
                                label={t("Password")}
                                isRequired={true}
                              />
                              <CustomField
                                name="password"
                                type="text"
                                placeholder={t("Password")}
                                touched={touched}
                                errors={errors}
                              />
                            </div>
                          </Col>
                        )}
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="national_id"
                              label={t("National Id")}
                              isRequired={true}
                            />
                            <CustomField
                              name="national_id"
                              type="text"
                              placeholder={t("National Id")}
                              touched={touched}
                              errors={errors}
                              isReadOnly={mode === "update"}
                            />
                          </div>
                        </Col>
                        {(mode === "add" ||
                          (mode === "update" &&
                            (showAll ||
                              permissions.includes("can_assign_role")))) && (
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="role_uuid"
                                label={t("Role")}
                                isRequired={true}
                              />
                              <Field
                                as="select"
                                name="role_uuid"
                                className={`form-control ${
                                  errors?.role_uuid && touched?.role_uuid
                                    ? "is-invalid"
                                    : ""
                                } `}
                              >
                                <option value="" label={t("Select Role")} />
                                {rolesList?.map(item => (
                                  <option
                                    value={item?.uuid}
                                    key={item?.uuid}
                                    label={item?.role}
                                  />
                                ))}
                              </Field>{" "}
                              <ErrorMessage
                                name={"role_uuid"}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </Col>
                        )}
                        <Col lg={12}>
                          <div className="g_end mt-2 gap-3 cis_btn">
                            <button
                              className="main_btn disabled-button fit_content px-4"
                              type="button"
                              onClick={() => navigate(-1)}
                              disabled={isLoading}
                            >
                              {t("Cancel")}
                            </button>
                            <button
                              className="main_btn fit_content px-4"
                              type="submit"
                              disabled={isLoading}
                            >
                              {mode === "add" ? t("Create") : t("Update")}
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Container>
        )}
      </div>
    </React.Fragment>
  )
}

export default CreateUser
